import { ArrowLeftOutlined, CheckCircleOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Descriptions, Empty, Skeleton, Space, Tag } from "antd";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { TaskCompletionList } from "../components/TaskCompletionList";
import { TaskEditModal } from "../components/TaskEditModal";
import { TaskStatusModal } from "../components/TaskStatusModal";
import { TASK_PRIORITY_LABELS, TASK_STATUS_COLORS, TASK_STATUS_OPTIONS } from "../constants";
import { useTask } from "../hooks/useTasks";
import { useTasksStore } from "../store/tasks.store";
import type { Task, TaskStatus } from "../types";

function formatDate(date: string | null | undefined): string {
	if (!date) {
		return "—";
	}
	return new Date(date).toLocaleDateString("uz-UZ", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});
}

function statusTag(status: string) {
	const label = TASK_STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
	return <Tag color={TASK_STATUS_COLORS[status as TaskStatus] ?? "default"}>{label}</Tag>;
}

function buildTaskInfoItems(task: Task) {
	return [
		{
			key: "status",
			label: "Status",
			children: (
				<Space size={4}>
					{statusTag(task.status)}
					{task.isOverdue && <Tag color="red">Muddati o'tgan</Tag>}
				</Space>
			),
		},
		{
			key: "priority",
			label: "Ustuvorlik",
			children: TASK_PRIORITY_LABELS[task.priority] ?? task.priority,
		},
		{ key: "type", label: "Turi", children: task.type?.name ?? task.typeId },
		{ key: "organization", label: "Tashkilot", children: task.organizationName ?? "—" },
		{
			key: "brigade",
			label: "Brigada",
			children: task.brigade
				? `${task.brigade.name}${task.brigade.leaderName ? ` (rahbar: ${task.brigade.leaderName})` : ""}`
				: "—",
		},
		{
			key: "targetType",
			label: "Turi",
			children: task.targetType === "LOCATION" ? "Joylashuv" : "Ekin",
		},
		{ key: "dueDate", label: "Muddat", children: formatDate(task.dueDate) },
		{ key: "createdBy", label: "Yaratdi", children: task.createdByUser?.fullname ?? "—" },
		{ key: "completedAt", label: "Bajarilgan", children: formatDate(task.completedAt) },
		{ key: "description", label: "Izoh", span: 2, children: task.description },
	];
}

export function TaskDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data: task, isLoading } = useTask(id ?? "");
	const { openEditModal, openStatusModal } = useTasksStore();

	if (!id) {
		return <Navigate to="/tasks" replace />;
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Button
						type="text"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(-1)}
						size="small"
						className="text-gray-500"
					/>
					{isLoading ? (
						<Skeleton.Input active size="small" style={{ width: 220 }} />
					) : (
						<h4 className="text-lg font-semibold m-0">{task?.title ?? "Topshiriq"}</h4>
					)}
				</div>
				{task && (
					<Space>
						<Button icon={<EditOutlined />} onClick={() => openEditModal(task)}>
							Tahrirlash
						</Button>
						<Button
							type="primary"
							icon={<CheckCircleOutlined />}
							onClick={() => openStatusModal(task)}
						>
							Statusni yangilash
						</Button>
					</Space>
				)}
			</div>

			<Card size="small">
				{isLoading ? (
					<Skeleton active paragraph={{ rows: 3 }} />
				) : task ? (
					<Descriptions size="small" column={2} items={buildTaskInfoItems(task)} />
				) : (
					<Empty description="Topshiriq topilmadi" />
				)}
			</Card>

			{task && (
				<Card
					size="small"
					title={
						<span className="font-semibold">
							{task.targetType === "LOCATION" ? "Joylashuvlar" : "Ekinlar"} va bajarilish holati
						</span>
					}
				>
					<TaskCompletionList task={task} />
				</Card>
			)}

			<TaskEditModal />
			<TaskStatusModal />
		</div>
	);
}
